import {
  Button,
  Checkbox,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { generatePath, useNavigate } from 'react-router-dom';

import { detachAdGroup, getAllByAdGroupId } from '../../api/ventura/product';
import { PRODUCTS_EDIT_URL, PRODUCTS_NEW_URL } from '../../constants/routeUrls';
import { useConfig } from '../../hooks/useConfig';
import useModal from '../../hooks/useModal';
import { camelCaseKeys } from '../../util/object';
import { headerStyles } from '../Contracts/ContractsTable';
import UnattachedProductsList from './UnattachedProductsList';

interface ProductsListProps {
  advertiserId: number;
}

function ProductsList(props: ProductsListProps) {
  const { advertiserId } = props;

  const { apiHost } = useConfig();
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const navigate = useNavigate();
  const [selected, setSelected] = React.useState([]);

  const form = useFormContext();
  const { getValues } = form;
  const [adGroupId, adGroupName] = getValues(['id', 'name']);

  const handlePageChange = (_event: unknown, value: number) => {
    setPage(value);
  };

  const handlePerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const query = useQuery({
    queryKey: ['products', adGroupId],
    queryFn: () => getAllByAdGroupId(apiHost, adGroupId),
    select: camelCaseKeys,
    enabled: Boolean(adGroupId),
  });

  const { data: productsList, isLoading, refetch } = query;

  const [showAddExistingProductModal, closeAddExistingProductModal] = useModal(
    () => {
      const onClose = () => {
        refetch();
        closeAddExistingProductModal();
      };
      return (
        <UnattachedProductsList
          adGroupId={adGroupId}
          adGroupName={adGroupName}
          advertiserId={advertiserId}
          onClose={onClose}
          existingProductList={productsList?.collection}
        />
      );
    },
    [productsList, adGroupId, adGroupName, advertiserId, refetch]
  );

  const detachProduct = async () => {
    // eslint-disable-next-line no-alert
    if (selected?.length > 0 && window.confirm('Are you sure you want to remove the selected products?')) {
      const promiseList = [];
      selected.forEach((productId) => {
        promiseList.push(detachAdGroup(apiHost, adGroupId, productId));
      });

      await Promise.all(promiseList);

      setSelected([]);

      refetch();
    }
  };

  const paginatedProductList = productsList?.collection.slice(page * perPage, page * perPage + perPage - 1);

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelected(paginatedProductList?.map((product) => product.productId));
      return;
    }
    setSelected([]);
  };

  const headerStylesCss = headerStyles();

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          InHand Products
        </Typography>
      </Stack>
      <Stack direction="row" spacing={1} justifyContent="end">
        {selected?.length > 0 && (
          <Button variant="outlined" onClick={() => detachProduct()}>
            Remove
          </Button>
        )}
        <Button variant="outlined" onClick={() => showAddExistingProductModal()}>
          Add Existing Product
        </Button>
        <Button onClick={() => navigate(generatePath(PRODUCTS_NEW_URL, { adGroupId: adGroupId.toString() }))}>
          Create New Product
        </Button>
      </Stack>
      <TableContainer className={headerStylesCss.root}>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead sx={{}}>
            <TableRow>
              <TableCell>
                <Checkbox
                  indeterminate={selected.length > 0 && selected.length < paginatedProductList?.length}
                  checked={productsList?.collection?.length > 0 && selected.length === paginatedProductList?.length}
                  onChange={handleSelectAllClick}
                />
              </TableCell>
              <TableCell>Name</TableCell>
              <TableCell align="left">Creative Count</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading &&
              Array.from({ length: perPage }, () => Math.random()).map((rowItem) => (
                <TableRow key={rowItem} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell colSpan={100}>
                    <Skeleton animation="wave" variant="text" sx={{ fontSize: '1rem' }} />
                  </TableCell>
                </TableRow>
              ))}
            {paginatedProductList?.map((product) => (
              <TableRow
                key={`link-${product.productId}`}
                onClick={() =>
                  navigate(
                    generatePath(PRODUCTS_EDIT_URL, { adGroupId: adGroupId.toString(), productId: product.productId })
                  )
                }
              >
                <TableCell>
                  <Checkbox
                    onClick={(event) => event.stopPropagation()}
                    checked={selected.indexOf(product.productId) !== -1}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelected([...selected, product.productId]);
                      } else {
                        setSelected(selected.filter((id) => id !== product.productId));
                      }
                    }}
                  />
                </TableCell>
                <TableCell>{product.name}</TableCell>
                <TableCell align="left">{product.creativeCount}</TableCell>
              </TableRow>
            ))}
            <TableRow key="pagination">
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                count={productsList?.pagination?.totalItems || 0}
                page={page}
                onPageChange={handlePageChange}
                rowsPerPage={perPage}
                onRowsPerPageChange={handlePerPageChange}
              />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}

export default ProductsList;
